import { appPoolRegionSchema, appPoolSpotCapacitySchema } from "./app-pool.js";
import { z } from "zod";
//#region src/billing.ts
/**
* CP Billing + Cost-Ledger wire contract (plan cp-billing-cost-ledger, WS1).
*
* Single source of truth for the billing vocabulary + DTOs shared across the
* feature. Seams:
*   - S3/S7: `CostLedgerEntry` mirrors the `metering.cost_ledger` columns 1:1
*     (D13 — camelCase of the column names, same column order). WS2 owns the
*     table; WS3 is the sole inserter; WS6 reads it typed to this DTO.
*   - S8 (D26): `updatePricingRatesSchema` is the WS1-owned rate-card WRITE DTO
*     (WS3 binds it on `PUT /v1/metering/pricing-rates`, gated `cp:billing:write`).
*     Rates use the UNSIGNED `billingRateStringSchema` (D25); signed
*     `billingDecimalStringSchema` stays for ledger `cost` / scale `costDelta`.
*   - D14: `billingResourceTypeSchema` is the billed resource vocabulary,
*     shared by `cost_ledger.resource_type` AND `pricing_rates.resource_type`.
*   - D15: `billingCloudProviderSchema` maps to `cost_ledger.cloud_provider`.
*
* All view DTOs are `.strict()` (#466). Money is a decimal STRING (Postgres
* `numeric` is returned as a string by node-postgres; never a float).
*/
/** Billed resource-type tokens (D14). The 3 base tokens price cpu/mem/storage; the
* rate card + the ledger FILTER share {@link billingResourceTypeSchema}, which since
* DEC-32 ALSO admits the `node_hours:<machineType>` grammar (below). */
const BILLING_RESOURCE_TYPES = [
	"cpu_core_hours",
	"memory_gb_hours",
	"storage_gb"
];
/**
* Node-hour rate-card resource-type grammar (DEC-32/DEC-5): `node_hours:<machineType>`,
* e.g. `node_hours:g2-standard-4`. A PATTERN, never a bounded SKU enum — machine types are
* dynamically region-discovered (only the machine-type FAMILY set is static). `<machineType>`
* is a lowercase GCE-style token (`[a-z0-9-]+`). Rate rows keyed by these price DEC-5
* nodeType-pool scale events (node-hours × rate); the 3 base tokens keep cpu/mem/storage pricing.
*/
const NODE_HOURS_RESOURCE_TYPE_PATTERN = /^node_hours:[a-z0-9-]+$/;
const billingResourceTypeSchema = z.union([z.enum(BILLING_RESOURCE_TYPES), z.string().regex(NODE_HOURS_RESOURCE_TYPE_PATTERN)]);
/**
* The value space of `cost_ledger.resource_type` (D23.1): the billed tokens
* PLUS the `'scale_event'` descriptor — a `kind='scale_event'` row carries
* `resource_type='scale_event'`, not a billing token. Used ONLY by
* `costLedgerEntrySchema` (and `costLedgerLineSchema`, derived via `.omit()`).
* This ledger-ROW vocabulary stays CLOSED — it is built from the
* `BILLING_RESOURCE_TYPES` tuple + `'scale_event'`, NOT from
* `billingResourceTypeSchema`, so DEC-32's node-hour widening does NOT reach it (a
* `kind='scale_event'` row carries `resourceType='scale_event'`; DEC-4 — pool
* identity rides `dp_scale_events`). The rate card (`pricingRateEntrySchema`) and
* the ledger filter (`billingLedgerFiltersSchema.resourceType`) share
* `billingResourceTypeSchema`, which since DEC-32 = the 3 base tokens ∪ the
* `node_hours:<machineType>` grammar (a node-hour filter value validates but matches
* no ledger row → empty result, not a 400).
*/
const COST_LEDGER_RESOURCE_TYPES = [...BILLING_RESOURCE_TYPES, "scale_event"];
const costLedgerResourceTypeSchema = z.enum(COST_LEDGER_RESOURCE_TYPES);
/**
* The cloud a cost belongs to (D15): the value of `cost_ledger.cloud_provider` and
* `pricing_rates.cloud_provider`, and the `cloudProvider` field of four wire schemas:
* `costLedgerEntrySchema`, `pricingRateEntrySchema`, `updatePricingRateInputSchema` and
* `costReconciliationEntrySchema`.
*
* Callers: site-admin-api filters the rate card with `cloud_provider = ANY(...)` over this
* tuple, so a row whose provider is not a member is absent from the rate card. The CP
* console offers these members, in this order, as the provider choice of a rate-card row.
*
* Members: `'gcp'`, `'local'` and `'aws'` (plan 2026-08-25-dp-aws-port, R-056 item 1). `'aws'`
* comes last, so the position of the two older members does not move for a caller that
* reads them by index. Any other string, `'azure'` and `''` included, is a `ZodError` at
* every schema that reads this enum. Pure data, no side effects.
*/
const BILLING_CLOUD_PROVIDERS = [
	"gcp",
	"local",
	"aws"
];
/** Runtime validator for {@link BILLING_CLOUD_PROVIDERS}. Each member parses to itself; any other value is a `ZodError`. */
const billingCloudProviderSchema = z.enum(BILLING_CLOUD_PROVIDERS);
/** Ledger scope discriminator (D13 column `scope`). */
const COST_LEDGER_SCOPES = [
	"cp",
	"dp",
	"org",
	"app"
];
const costLedgerScopeSchema = z.enum(COST_LEDGER_SCOPES);
/** Ledger row kind (D13 column `kind`). */
const COST_LEDGER_KINDS = ["rollup", "scale_event"];
const costLedgerKindSchema = z.enum(COST_LEDGER_KINDS);
/**
* SIGNED money as a decimal string (Postgres `numeric`). Used ONLY for ledger
* `cost` (a scale-DOWN row's cost delta is legitimately negative). Optional
* leading `-`, one optional fractional part; no exponent, no thousands
* separators. Rates use the UNSIGNED `billingRateStringSchema` below (D25).
*/
const billingDecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/);
/**
* UNSIGNED money string for RATES (D25). An applied/carded rate can NEVER be
* negative — a negative rate would invert cost across the whole ledger, so
* strict validation here is a bug-catcher. Used by `ratePerUnit` EVERYWHERE it
* appears: `CostLedgerEntry.ratePerUnit`, `PricingRateEntry.ratePerUnit`, and
* the rate-card write DTO (`updatePricingRateInputSchema`). The signed
* `billingDecimalStringSchema` stays for ledger `cost` (a scale-DOWN row's cost
* is legitimately negative); `usageQuantity` is a signed `z.number()` (a
* scale-DOWN Δreplicas is negative).
*/
const billingRateStringSchema = z.string().regex(/^\d+(\.\d+)?$/);
/**
* Worker size classes (mirrors `WorkerSizeClass` — the `satisfies` below is a
* compile-time drift guard so this tuple can never diverge from that union).
*/
const WORKER_SIZE_CLASSES = [
	"small",
	"medium",
	"large",
	"xlarge"
];
const workerSizeClassSchema = z.enum(WORKER_SIZE_CLASSES);
/**
* One immutable ledger line item. Field NAMES + ORDER are the camelCase of the
* D13 (amended by D20.2) `cost_ledger` column contract; WS2's Drizzle table
* MUST match 1:1. `cost` is denormalized (= usageQuantity * ratePerUnit at
* write time, D13). `sourceEventId` is the D20.2 scale-event idempotency key
* (the release.dp_scale_events row / event id that produced a
* `kind='scale_event'` row): NULL for `kind='rollup'` rows, populated for
* scale events. Nullable dims: exactly one of the scope dims is populated per
* row (serviceName for scope='cp', dataPlaneId for 'dp', orgId for 'org',
* appName for 'app').
*
* Migration 0009 of the metering bundle (plan 2026-10-12-console-ward-billing, R-003) adds
* two writer columns after `created_at`: `unpriced` and `idempotency_key`. This DTO names
* neither. The read API turns `unpriced` into `CostLedgerLine.priced`, and
* `idempotency_key` is the writer's key ({@link costLedgerRollupKey}). The mirror is 1:1
* for every column up to `created_at`.
*/
const costLedgerEntrySchema = z.object({
	id: z.uuid(),
	scope: costLedgerScopeSchema,
	kind: costLedgerKindSchema,
	sourceEventId: z.number().int().nullable(),
	serviceName: z.string().nullable(),
	dataPlaneId: z.uuid().nullable(),
	orgId: z.uuid().nullable(),
	appName: z.string().nullable(),
	resourceType: costLedgerResourceTypeSchema,
	cloudProvider: billingCloudProviderSchema,
	usageQuantity: z.number(),
	unit: z.string().min(1),
	ratePerUnit: billingRateStringSchema,
	cost: billingDecimalStringSchema,
	currency: z.string().min(1),
	periodStart: z.string().datetime(),
	periodEnd: z.string().datetime(),
	createdAt: z.string().datetime()
}).strict();
/**
* A single rate-card row (mirrors `metering.pricing_rates`:
* cloud/cp/site-admin-api/src/db/schema/pricing-rates.ts). `resourceType` uses
* the D14 shared billing vocabulary — WS2/WS3 align the pricing_rates seed to
* these exact tokens (D14), so every row resolves under the enum; a legacy
* non-vocabulary row would 400 the read, which is the intended post-D14 state.
* `ratePerUnit` is the Postgres `numeric` decimal string. Consumed by WS6's
* pricing-rates read endpoint (`GET v1/admin/billing/pricing-rates`, S8 read
* side) + WS8's pricing MCP tool.
*
* `region` is the row's `metering.pricing_rates.region` column (2026-10-02-regional-pools-release-2
* §2.2, DF-1). It is always present on the read view. `null` is the home row, the rate every
* region without its own row falls back to. A string is a regional row in
* `APP_POOL_REGION_PATTERN`'s grammar, through `appPoolRegionSchema` (DF-2), and a value outside
* the grammar fails the parse. The key is required, so a producer that forgets the column fails a
* `.strict()` parse and does not read as a home row.
*
* `capacityType` is the row's `metering.pricing_rates.capacity_type` column
* (2026-10-24-pool-machine-generations, D-36). `'spot'` is the spot rate of the machine type,
* through `appPoolSpotCapacitySchema`. The absent key is the on-demand rate. The key is optional
* on this read view, where `region` is required: a producer written before the column existed
* still parses, and reads as on-demand. Any other value, `'on-demand'` and `null` included, fails
* the parse at `capacityType`. The resource type grammar does not move: a spot rate keys by
* `node_hours:<machineType>` and this member, never by a suffix in the resource type.
*/
const pricingRateEntrySchema = z.object({
	id: z.uuid(),
	cloudProvider: billingCloudProviderSchema,
	resourceType: billingResourceTypeSchema,
	region: appPoolRegionSchema.nullable(),
	capacityType: appPoolSpotCapacitySchema.optional(),
	ratePerUnit: billingRateStringSchema,
	currency: z.string().min(1),
	effectiveFrom: z.string().datetime(),
	createdAt: z.string().datetime()
}).strict();
/**
* One rate-card row on the write path (D26). D14/D15-constrained + non-negative
* (D25): the legacy platform-api DTO accepted a free-string `resourceType` and
* NEGATIVE rates — a financial-write hole this closes. `.strict()`.
*
* `region` is optional (2026-10-02-regional-pools-release-2 §2.2, D-13, DF-1). Absent writes the
* home row, `region IS NULL`, which is every write before the field existed. Present, it writes a
* regional row for that cloud region, and it must match `APP_POOL_REGION_PATTERN` through
* `appPoolRegionSchema`; the pipe answers 400 for any other string. `null` is a parse error: the
* home row has one spelling on the write path, the absent key. The grammar says nothing about
* whether a satellite exists in that region (D-13: no fleet-wide region list exists).
*
* `capacityType` is optional (2026-10-24-pool-machine-generations, D-36). Absent writes the
* on-demand row, which is every write before the field existed. `'spot'` writes the spot row of
* the same machine type. Any other value, `'on-demand'` and `null` included, is a parse error, so
* on-demand has one spelling on the write path, the absent key.
*
* Only a node-hour rate has a spot row (D-36): spot capacity is a property of a node group's
* machine type, so `'spot'` is legal only beside a `resourceType` in
* {@link NODE_HOURS_RESOURCE_TYPE_PATTERN}. A spot write on `cpu_core_hours`, `memory_gb_hours`
* or `storage_gb` fails with one issue at `capacityType` (DEF-1: refuse over accept), so the
* write path never stores a spot row that no spot node-hour lookup reads.
*/
const updatePricingRateInputSchema = z.object({
	cloudProvider: billingCloudProviderSchema,
	resourceType: billingResourceTypeSchema,
	region: appPoolRegionSchema.optional(),
	capacityType: appPoolSpotCapacitySchema.optional(),
	ratePerUnit: billingRateStringSchema,
	currency: z.string().min(1)
}).strict().refine((row) => row.capacityType === void 0 || NODE_HOURS_RESOURCE_TYPE_PATTERN.test(row.resourceType), {
	path: ["capacityType"],
	message: "capacityType 'spot' is legal only on a node_hours:<machineType> resource type"
});
/**
* `PUT /v1/metering/pricing-rates` body (D26). WS3 binds this on the controller
* (replacing the legacy free-string DTO), keeps the append-only insert, and
* adds an audit-log entry; WS7's rate-card editor imports
* `UpdatePricingRatesRequest`. Wrapper shape `{ rates: [...] }` matches the
* existing controller binding so the swap is drop-in.
*/
const updatePricingRatesSchema = z.object({ rates: z.array(updatePricingRateInputSchema).min(1) }).strict();
/**
* One summary bucket: a group value and its total cost. `key` is the dimension
* value — `'cp'` or a serviceName for `byScope`, a D14 resource-type token for
* `byResourceType`, a dpId for `byDataPlane`, an appName for `byApp`.
*/
const costSummaryBucketSchema = z.object({
	key: z.string(),
	cost: billingDecimalStringSchema
}).strict();
/**
* The once-counted cost summary of one read window. The site-admin-api billing read
* (`GET /v1/admin/billing/summary` and the `billing_summary` MCP tool) produces it. The CP
* console Cost page, the tenant pages and the MCP clients read it.
*
* Fields (plan 2026-10-12-console-ward-billing, D7, D10, R-001, R-002):
* - `grandTotalCost`: the exact `numeric` sum of the `kind='rollup'` rows of the population
*   that {@link headlineScopes} names, with each natural key of a rollup row counted once.
*   With no `scope`, `appName` or `orgId` filter the population is `dp` plus `cp`
*   ({@link HEADLINE_SCOPES}). An `app` or `org` row is a slice of a `dp` row, so the total
*   never adds it beside its `dp` row.
* - `byScope`, `byResourceType`, `byDataPlane`, `byApp`: one bucket per key, each cost a
*   signed decimal string. `byDataPlane` has no bucket for a row with no data plane, and
*   `byApp` has no bucket for a row with no app.
* - `unattributed`: one signed decimal string per breakdown. For each breakdown, the sum of
*   its bucket costs plus its `unattributed` value equals `grandTotalCost` exactly. The value
*   is a zero decimal string (`'0'` or `'0.00'`: Postgres keeps the scale of the operands)
*   when the buckets cover the whole total. A negative value is legal: it shows that
*   the `app` rows of a window exceed their `dp` rows, and the producer never clamps it.
* - `scaleRunRateChange`: the sum of the `kind='scale_event'` rows. Each such row is a change
*   of the hourly run rate, `(to - from) x hourly rate`, so the sum is a rate change per
*   hour and never money spent. The total never adds it to `grandTotalCost` (D20.4, D10).
* - `scaleEventCost`: deprecated alias of `scaleRunRateChange` (R-001). It carries the same
*   string. A later release removes it. New code reads `scaleRunRateChange`.
* - `currency`: the currency of the headline rows, `'USD'` when the window holds none.
*
* Parse failures: an unknown key at any level (`.strict()`) and a money field that is not a
* signed decimal string. The producer keeps `scaleEventCost` equal to `scaleRunRateChange`;
* the schema holds no refinement, so `.omit()`, `.pick()` and `.partial()` stay usable (R-018).
*/
const costSummarySchema = z.object({
	byScope: z.array(costSummaryBucketSchema),
	byResourceType: z.array(costSummaryBucketSchema),
	byDataPlane: z.array(costSummaryBucketSchema),
	byApp: z.array(costSummaryBucketSchema),
	unattributed: z.object({
		byScope: billingDecimalStringSchema,
		byResourceType: billingDecimalStringSchema,
		byDataPlane: billingDecimalStringSchema,
		byApp: billingDecimalStringSchema
	}).strict(),
	grandTotalCost: billingDecimalStringSchema,
	scaleRunRateChange: billingDecimalStringSchema,
	/** @deprecated Alias of `scaleRunRateChange` with the same string (R-001). Read `scaleRunRateChange`. */
	scaleEventCost: billingDecimalStringSchema,
	currency: z.string()
}).strict();
/**
* Filters are applied over ALREADY-authorized platform-admin data — they are
* NOT authz inputs (security-audit threat #3). uuid/date/label-safe validation
* hardens the downstream SQL/PromQL (threats #4/#5). Query params arrive as
* strings, so `limit` coerces. `startDate`/`endDate` are full ISO-8601 UTC
* datetimes (e.g. `2026-07-01T00:00:00.000Z`).
*/
const billingLedgerFiltersSchema = z.object({
	scope: costLedgerScopeSchema.optional(),
	dataPlaneId: z.uuid().optional(),
	orgId: z.uuid().optional(),
	appName: z.string().max(63).regex(/^[a-z0-9]([a-z0-9._-]*[a-z0-9])?$/).optional(),
	resourceType: billingResourceTypeSchema.optional(),
	kind: costLedgerKindSchema.optional(),
	startDate: z.string().datetime(),
	endDate: z.string().datetime(),
	granularity: z.enum(["hourly", "daily"]).default("daily"),
	cursor: z.string().min(1).optional(),
	limit: z.coerce.number().int().positive().max(500).optional()
}).strict();
/**
* The scale-events query (plan 2026-10-12-console-ward-billing, R-023): the window,
* `dataPlaneId`, `appName` and `limit` (1 to 500). The read honours only these keys, so the
* schema refuses `scope`, `orgId`, `resourceType`, `kind` and `cursor` with a parse failure in
* place of a silent, unfiltered answer.
*
* `granularity` is inert: the schema accepts `hourly` or `daily` and the read ignores it. A
* console one release behind sends `granularity=daily`, and a refusal would break its Scale
* events tab. The key has no default, so a query without it parses without it. Any other
* value of the key is a parse failure.
*/
const billingScaleEventsQuerySchema = billingLedgerFiltersSchema.pick({
	dataPlaneId: true,
	appName: true,
	startDate: true,
	endDate: true,
	limit: true
}).extend({ granularity: z.enum(["hourly", "daily"]).optional() }).strict();
/**
* The ledger scopes that the headline total counts when the caller names no narrower
* population: `dp`, then `cp` (the order of D7).
*
* A `dp` rollup row holds the whole namespace cost of one data plane, and a `cp` rollup row
* holds the cost of one CP service, so the two scopes never hold the same cost. An `org` or
* an `app` row is a slice of a `dp` row, so this list leaves both out.
*
* Callers: {@link headlineScopes}, the site-admin-api billing read (WS-2) and the CP console
* Cost page (WS-3). The array is frozen at run time, so no caller can change it.
*/
const HEADLINE_SCOPES = Object.freeze(["dp", "cp"]);
/**
* The ledger scopes of the population that one billing query counts. The first rule that
* matches decides:
* 1. `scope` present: `[scope]`.
* 2. `appName` present: `['app']`.
* 3. `orgId` present: `['org']`.
* 4. Otherwise: {@link HEADLINE_SCOPES}, `['dp', 'cp']`.
*
* A field is present when its value is not `undefined`. The other filters
* (`dataPlaneId`, `resourceType`, the window) narrow the rows and never change the scopes.
* No output holds two scopes that overlap, so a sum over the output counts each cost once.
*
* Callers: the site-admin-api summary and trend reads (WS-2) and the Cost page population
* caption and chart keys (WS-3). Pure: it reads only its argument and never throws. The
* returned array is frozen.
*/
function headlineScopes(filters) {
	if (filters.scope !== void 0) return Object.freeze([filters.scope]);
	if (filters.appName !== void 0) return Object.freeze(["app"]);
	if (filters.orgId !== void 0) return Object.freeze(["org"]);
	return HEADLINE_SCOPES;
}
/**
* The largest page the ledger read returns: 200 lines. `billingLedgerQuerySchema` refuses a
* larger `limit`, and the site-admin-api service repeats the check for a direct caller with
* a 400 `limit must be at most 200`. No reader clamps a larger value in silence (R-002).
*/
const BILLING_LEDGER_MAX_LIMIT = 200;
/**
* The query of `GET /v1/admin/billing/ledger` and of the `billing_query_ledger` MCP tool.
* It is {@link billingLedgerFiltersSchema} with one change: `limit` is an integer from 1 to
* {@link BILLING_LEDGER_MAX_LIMIT}. Query strings coerce, so `'200'` parses to `200`.
*
* Parse failures: a `limit` of 0, a negative or fractional `limit`, a `limit` above 200, and
* every failure of the filters schema (an unknown key, a missing date and the others). The
* other billing routes keep the filters schema and its bound of 500.
*/
const billingLedgerQuerySchema = billingLedgerFiltersSchema.extend({ limit: z.coerce.number().int().positive().max(200).optional() });
/**
* The idempotency key of one `kind='rollup'` row of `metering.cost_ledger`, the value of the
* `idempotency_key` column. The platform-api metering writer (WS-1) sets it on every rollup
* row it inserts. The unique index `idx_cost_ledger_idempotency_key` then refuses a second
* row with the same key, and the writer inserts with `ON CONFLICT DO NOTHING`.
*
* Grammar, nine fields joined by `|`:
* `rollup|<scope>|<serviceName>|<dataPlaneId>|<orgId>|<appName>|<resourceType>|<cloudProvider>|<periodStart>`.
* A `null` field is the empty string. `periodStart` is `Date.prototype.toISOString()`, in
* UTC. The fields after `rollup` are the natural key of a rollup row, the same key that the
* view `metering.cost_ledger_once_counted` counts once. So the writer and the read agree on
* which rows are one cost.
*
* Errors, each a `RangeError` before any output:
* - a text field that contains `|`, because the key would then be ambiguous;
* - a text field that is the empty string, because `''` is the spelling of `null`;
* - a `periodStart` that is an invalid `Date`.
*
* Pure: the same input gives the same key. A `kind='scale_event'` row carries no key from
* this function; its `source_event_id` is its idempotency key.
*/
function costLedgerRollupKey(row) {
	if (Number.isNaN(row.periodStart.getTime())) throw new RangeError("costLedgerRollupKey: periodStart is an invalid Date");
	const fields = [
		["serviceName", row.serviceName],
		["dataPlaneId", row.dataPlaneId],
		["orgId", row.orgId],
		["appName", row.appName],
		["resourceType", row.resourceType],
		["cloudProvider", row.cloudProvider]
	];
	for (const [name, value] of fields) {
		if (value === "") throw new RangeError(`costLedgerRollupKey: ${name} is the empty string, the spelling of null`);
		if (value !== null && value.includes("|")) throw new RangeError(`costLedgerRollupKey: ${name} contains the separator '|'`);
	}
	return [
		"rollup",
		row.scope,
		...fields.map(([, value]) => value ?? ""),
		row.periodStart.toISOString()
	].join("|");
}
/**
* A ledger LINE as returned by the paged read (D22.2). Daily (and hourly) rows
* are period AGGREGATES, not raw table rows, so the per-row `id` and
* `createdAt` are omitted. Every other field (incl. `sourceEventId`) is
* unchanged. `.omit()` and `.extend()` keep the `.strict()` mode, so the schema refuses a
* line that has `id` or `createdAt` (zod 4.4.x).
*
* `priced` (plan 2026-10-12-console-ward-billing, R-002): `true` when every row of the
* line found its rate. `false` when one or more rows of the line missed their rate: the row
* carries `unpriced = true`, or a `rate_per_unit` of 0. The `cost` of an unpriced row is 0,
* so a `false` line reads "No rate" or "partly priced" in the console, never `$0.00`.
* Required: a line with no `priced` key fails the parse.
*/
const costLedgerLineSchema = costLedgerEntrySchema.omit({
	id: true,
	createdAt: true
}).extend({ priced: z.boolean() });
const billingLedgerPageSchema = z.object({
	items: z.array(costLedgerLineSchema),
	nextCursor: z.string().nullable(),
	totalCount: z.number().int().nonnegative()
}).strict();
/**
* A pool replica change. Feeds a `cost_ledger` row tagged `kind='scale_event'`
* (D8) — but the RAW event itself carries NO cost (DEC-16/DEC-27: WS-6's
* aggregator computes cost — cpu/mem for base pools, DEC-5 node-hours for nodeType
* pools — when writing the ledger row). `dataPlaneId` is the canonical platform DP
* uuid (same id used by `metering.usage_records`).
*
* `scaleEventSchema` is a **discriminated union on `poolKind`** (DEC-16): a
* `'worker'` event carries `workerName`; a `'service'` event carries `serviceName`;
* an `'app-pool'` event carries `groupId`.
* The discriminator makes "exactly one identity per event" a STRUCTURAL guarantee
* (no nullable-both flat object) — worker events stay type-safe while the union
* also covers service/custom pools.
*
* 2026-09-04-app-pools WS-3 (I-12, R-056) — the third branch. An app-pool node group's
* scale is a NODE count and not a replica count, and its identity is an `apg-` group id
* rather than a worker or a service: the group is derived from `(appKey, orgId, workload,
* nodeType)` and outlives every app version that declared its pool. So it can carry
* neither of the other two branches' required identity fields, and `fromReplicas` /
* `toReplicas` count NODES on this branch where they count PODS on the other two —
* `pool_kind` is what says which unit a row counts. `nodeType` stays OPTIONAL, as on
* every branch: a group always resolves a machine type in practice, but this is a
* DISPLAY shape and the pricing reads `dp_scale_events.node_type` directly, so a stored
* row missing it must stay parseable rather than take a whole billing page down.
* ⛔ The branch is what stops a `pool_kind='app-pool'` row reading as a service event.
* `billing-admin.service.ts`'s mapper discriminated on `pool_kind === 'worker'` with
* `'service'` as the else arm, so before this branch existed an app-pool row arrived at
* the console labelled a service whose name was the literal `pool-group`.
*
* `version` and `sizeClass` are `z.string().nullable()` (D30): the source
* `dp_scale_events.version` / `.size_class` columns are NULLABLE free text
* (D20.3), and CP size-class NAMES are operator-defined (the desired-state
* `SizeClassMap`), NOT a closed enum — so this must NOT reject a null or an
* unlisted class at read time. (`workerSizeClassSchema` stays exported for
* other uses; it is just not the type of this field.)
*
* The app-pool branch carries an optional `capacityType` (2026-10-24-pool-machine-generations,
* D-35, D-36): `'spot'` when the node group runs on spot capacity, absent when it runs on-demand.
* A row stored before the field existed reads as on-demand. The worker and service branches carry
* no capacity type, and their `.strict()` refuses the key.
*/
const scaleEventShared = {
	dataPlaneId: z.uuid(),
	appName: z.string().min(1),
	/** DP-unique billing DISPLAY key for the pool (DEC-27 — the per-pool rollup key
	*  the AutoscaleView groups + labels by). Required on every event. */
	poolName: z.string().min(1),
	version: z.string().nullable(),
	fromReplicas: z.number().int().nonnegative(),
	toReplicas: z.number().int().nonnegative(),
	sizeClass: z.string().nullable(),
	/** Machine type backing the pool (a `FixtureService.nodeType`/worker `nodeType`
	*  value); OMITTED when the pool runs on the DP BASE pool (node-pool D4). Nullable
	*  free text is avoided per DEC-16's `nodeType?: string` — use OPTIONAL. */
	nodeType: z.string().min(1).optional(),
	occurredAt: z.string().datetime()
};
const scaleEventSchema = z.discriminatedUnion("poolKind", [
	z.object({
		poolKind: z.literal("worker"),
		/** Required in the worker branch (keeps shipped worker events type-safe). */
		workerName: z.string().min(1),
		...scaleEventShared
	}).strict(),
	z.object({
		poolKind: z.literal("service"),
		/** Required in the service branch (custom `external`-scaled / HTTP / GPU pool). */
		serviceName: z.string().min(1),
		...scaleEventShared
	}).strict(),
	z.object({
		poolKind: z.literal("app-pool"),
		/** Required in the app-pool branch: the `apg-` node-group id, the group's whole identity.
		*  `poolName` carries the same value as the billing display key. */
		groupId: z.string().min(1),
		...scaleEventShared,
		/** `'spot'` for a node group on spot capacity; absent for on-demand (D-35). */
		capacityType: appPoolSpotCapacitySchema.optional()
	}).strict()
]);
/**
* The billing READ-DTO (DEC-27) — the raw union + `costDelta` from the
* `cost_ledger` JOIN. This is the `/v1/admin/billing/scale-events` response, the
* AutoscaleView, and WS-7's E2E consume THIS (`scaleEventSchema` carries no cost).
* A `discriminatedUnion` cannot be `.extend()`ed, so mirror the two branches +
* `costDelta`.
*
* `costDelta` is a change of the hourly run rate, `(to - from) x hourly rate`, and never
* money spent (plan 2026-10-12-console-ward-billing, D10).
*
* `priced` (R-002), on every branch: `true` when the joined ledger row found every rate it
* needs. `false` when the row carries `unpriced = true` or a `rate_per_unit` of 0. The
* writer then stores a `costDelta` of 0, so the console shows "No rate" for a `false` event
* and never a run-rate change of zero. Required on every branch.
*/
const scaleEventWithCostSchema = z.discriminatedUnion("poolKind", [
	z.object({
		poolKind: z.literal("worker"),
		workerName: z.string().min(1),
		...scaleEventShared,
		/** Per-event cost delta from the `cost_ledger` JOIN (signed decimal string —
		*  scale-down is negative). Present only on the READ shape. */
		costDelta: billingDecimalStringSchema,
		priced: z.boolean()
	}).strict(),
	z.object({
		poolKind: z.literal("service"),
		serviceName: z.string().min(1),
		...scaleEventShared,
		costDelta: billingDecimalStringSchema,
		priced: z.boolean()
	}).strict(),
	z.object({
		poolKind: z.literal("app-pool"),
		groupId: z.string().min(1),
		...scaleEventShared,
		/** `'spot'` for a node group on spot capacity; absent for on-demand (D-35). */
		capacityType: appPoolSpotCapacitySchema.optional(),
		costDelta: billingDecimalStringSchema,
		priced: z.boolean()
	}).strict()
]);
/**
* App-filter option (D24.E1) — WS6 `GET v1/admin/billing/apps`. `id` is the
* app name/slug used as the `appName` filter value; `label` is the display
* string. Deliberately lenient bare strings (a read-view of stored reality).
*/
const billingAppOptionSchema = z.object({
	id: z.string(),
	label: z.string()
}).strict();
/** Group-by dimension for the cost-trend query (D24.E6). Snake_case wire
* values (distinct from the CostSummary dimension keys). */
const BILLING_TREND_GROUP_BY = [
	"scope",
	"resource_type",
	"data_plane",
	"app"
];
const billingTrendGroupBySchema = z.enum(BILLING_TREND_GROUP_BY);
/**
* One time-bucket of the cost trend (D24.E6): a period start, the bucket
* total (the once-counted headline population of the query) and the
* per-dimension `segments`. For groupBy scope and resource_type the segments
* sum to the total. For data_plane and app they need not: total minus the
* segment sum is the unattributed share, which can be negative. `periodStart` is a
* plain string (server-formatted bucket label, hourly/daily) — intentionally
* NOT constrained to ISO here.
*/
const costTrendBucketSchema = z.object({
	periodStart: z.string(),
	total: billingDecimalStringSchema,
	segments: z.array(z.object({
		key: z.string(),
		cost: billingDecimalStringSchema
	}).strict())
}).strict();
/** The full cost trend — one bucket per period (D24.E6). */
const costTrendSchema = z.array(costTrendBucketSchema);
/**
* Cost-trend query (D24.E6) = the ledger filters REUSED VERBATIM via `.extend()`
* (all filter fields inherited — never redefined) PLUS a `groupBy` dimension
* defaulting to `'scope'`. `.extend()` preserves `.strict()` (verified zod
* 4.4.x), so unknown query keys are still rejected; the inherited
* `startDate`/`endDate`/`granularity` stay required.
*/
const billingTrendQuerySchema = billingLedgerFiltersSchema.extend({ groupBy: billingTrendGroupBySchema.default("scope") });
/**
* Reconciliation granularity tier (D32). `project` = T1 (whole-project total,
* always produced), `service` = T2 (per-CP-service, needs GKE cost allocation),
* `dp` = T3 (per-data-plane, needs the `dp_id` composition label). T2/T3 are
* separable sub-stages; the reconciler emits no rows for them until their labels
* exist (graceful).
*/
/**
* Reconciliation tiers the reconciler can emit.
*
* ⚠️ `node_pool` (T4) was added 2026-08-02 and this enum is the reason it must be:
* `granularity` is a CLOSED enum (unlike `scope`, which is deliberately a lenient
* `z.string()` — see the note below), so a tier absent from this list is rejected by
* `costReconciliationEntrySchema` and cannot be requested through
* `billingReconciliationQuerySchema`. Adding a tier to the reconciler WITHOUT adding
* it here writes rows that the contract declares invalid.
*
* Widening is a MINOR, not a breaking change: verified no consumer switches
* exhaustively on this union — `tierLabel()` is a ternary chain with a fallback and
* `RECON_SCOPE_LABELS` is `Record<string,string>` with a `?? entry.scope` fallback, so
* neither fails to compile. The site-admin filter bar renders only `TIER_ORDER`
* (`project`/`service`/`dp`), so a `node_pool` row is never fetched by the current UI.
*/
const COST_RECONCILIATION_GRANULARITIES = [
	"project",
	"service",
	"dp",
	"node_pool"
];
const costReconciliationGranularitySchema = z.enum(COST_RECONCILIATION_GRANULARITIES);
/**
* One append-only reconciliation row (D31, amended by D35). 1:1 camelCase of the
* `metering.cost_reconciliation` columns in D31 order. Written ONLY by the
* platform-api reconciler (D3); read by site-admin-api (WS9a read endpoint + MCP).
*
* Money is a DECIMAL STRING (Postgres `numeric`) — NEVER `Number()`:
*   - `estimatedCost` UNSIGNED (`billingRateStringSchema`): summed usage × a
*     non-negative rate is always ≥ 0.
*   - `actualCost` SIGNED (`billingDecimalStringSchema`, D35.3): the gross cost of the
*     Cloud Billing export, before credits. The platform-api reconciler sums `cost` and
*     nets no credit since 2026-08-02 (plan 2026-10-12-console-ward-billing, R-002, ARC-23).
*     The schema stays signed, so no stored row of the earlier credits-netted reconciler
*     fails the read.
*   - `variance` SIGNED (`billingDecimalStringSchema`) = `actualCost − estimatedCost`.
*
* `actualAvailable` (R-002): `true` when the export held a cost for the row's period and
* scope. `false` when the export held no row, a SQL NULL or an unreadable value. Then the
* reconciler stores `actualCost` `'0'` and `variance` `'0'`, and no consumer reads either
* field: the console shows "Not available". A row from before migration 0009 has a NULL
* column, and the read API sends `false` for it when its stored actual is exactly 0.
* Required: a row with no `actualAvailable` key fails the parse.
*
* VARIANCE SEMANTICS (D35.1): `variance = actual(the WHOLE zion-cp GCP bill) −
* estimated(only the metered CPU/mem/storage)`. The bill legitimately includes
* un-metered spend (Cloud SQL, Memorystore, Artifact Registry, network egress/LB,
* KMS — overview Out-of-Scope), so a LARGE POSITIVE T1 variance is EXPECTED. This
* is an actual-vs-estimate annotation, never an "estimate should equal actual" check.
*
* `scope` is a lenient `z.string()` (NOT an enum): this reconciler produces ONLY
* `project` (T1), `cp` (T2), and `dp` (T3) — there is NO org/app reconciliation
* tier and NO dp_id→org join (D36.3). The D31 column is `text`; keeping the DTO a
* plain string (not a closed enum) means a future scope value never 400s the read
* (mirrors BillingAppOption's lenient-read + the D30 relaxation rationale).
*/
const costReconciliationEntrySchema = z.object({
	id: z.uuid(),
	periodStart: z.string().datetime(),
	periodEnd: z.string().datetime(),
	granularity: costReconciliationGranularitySchema,
	scope: z.string(),
	serviceName: z.string().nullable(),
	dataPlaneId: z.uuid().nullable(),
	cloudProvider: billingCloudProviderSchema,
	gcpService: z.string().nullable(),
	estimatedCost: billingRateStringSchema,
	actualCost: billingDecimalStringSchema,
	variance: billingDecimalStringSchema,
	currency: z.string().min(1),
	billingExportTime: z.string().datetime(),
	createdAt: z.string().datetime(),
	actualAvailable: z.boolean()
}).strict();
/**
* `GET /v1/admin/billing/reconciliation` query (WS9a). Field names are `startDate`/
* `endDate` (NOT `periodStart`/`periodEnd`) — CONSISTENT with every other billing
* query (`billingLedgerFiltersSchema`, D27/D37.1); the service maps them onto the
* `period_start`/`period_end` COLUMNS (the columns keep their names). `startDate`/
* `endDate` REQUIRED (bounded). `granularity` DEFAULTS to `'project'` (D37.1 — the
* default view is the always-produced project tier). `scope`/`dataPlaneId` are
* optional filters over already-authorized platform-admin data (never authz
* inputs); `dataPlaneId` is an OPTIONAL server param wired for T3 (dp-granularity)
* narrowing to one data plane (D37.1) — WS9c does NOT send it. `.strict()`.
*/
const billingReconciliationQuerySchema = z.object({
	startDate: z.string().datetime(),
	endDate: z.string().datetime(),
	granularity: costReconciliationGranularitySchema.default("project"),
	scope: z.string().optional(),
	dataPlaneId: z.uuid().optional()
}).strict();
//#endregion
export { BILLING_CLOUD_PROVIDERS, BILLING_LEDGER_MAX_LIMIT, BILLING_RESOURCE_TYPES, BILLING_TREND_GROUP_BY, COST_LEDGER_KINDS, COST_LEDGER_RESOURCE_TYPES, COST_LEDGER_SCOPES, COST_RECONCILIATION_GRANULARITIES, HEADLINE_SCOPES, NODE_HOURS_RESOURCE_TYPE_PATTERN, WORKER_SIZE_CLASSES, billingAppOptionSchema, billingCloudProviderSchema, billingDecimalStringSchema, billingLedgerFiltersSchema, billingLedgerPageSchema, billingLedgerQuerySchema, billingRateStringSchema, billingReconciliationQuerySchema, billingResourceTypeSchema, billingScaleEventsQuerySchema, billingTrendGroupBySchema, billingTrendQuerySchema, costLedgerEntrySchema, costLedgerKindSchema, costLedgerLineSchema, costLedgerResourceTypeSchema, costLedgerRollupKey, costLedgerScopeSchema, costReconciliationEntrySchema, costReconciliationGranularitySchema, costSummaryBucketSchema, costSummarySchema, costTrendBucketSchema, costTrendSchema, headlineScopes, pricingRateEntrySchema, scaleEventSchema, scaleEventWithCostSchema, updatePricingRateInputSchema, updatePricingRatesSchema, workerSizeClassSchema };

//# sourceMappingURL=billing.js.map